import type { OnDestroy, OnInit } from '@angular/core';
import { Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { filter, startWith, switchMap, take, tap, map } from 'rxjs';

import { DetachedRouterOutlet, filterBoolean } from '@core/common-legacy';
import type { SelectOption } from '@core/user-interface-legacy';
import { Select2Component } from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import {
  DataSourceActions,
  DataSourceListActions,
  DataSourceListSelectors,
  DataSourceSelectors,
  ViewListActions,
} from '@core/views-data';
import {
  dataSourceProvider$,
  dataSourceServiceMap,
  dataSourceServiceProvider$,
  INITIAL_DS_ID,
} from '@core/views-util';

export const canActivateDataSource = () => () => {
  const store = inject(Store);
  const dataSourceProvider = inject(dataSourceProvider$);
  const initialId = inject(INITIAL_DS_ID);

  store.dispatch(ViewListActions.fetchCollection({ payload: { dataSourceId: initialId } }));
  store.dispatch(DataSourceActions.fetchOne({ payload: { id: initialId } }));

  return store.select(DataSourceSelectors.meta(initialId)).pipe(
    filter(meta => meta.isLoaded && !meta.isPending),
    switchMap(() => store.select(DataSourceSelectors.byId(initialId))),
    filterBoolean,
    tap(dataSource => {
      if (dataSourceProvider.value === dataSource) return;

      dataSourceProvider.next(dataSource);
    }),
    take(1),
    map(() => true),
  );
};

@Component({
  selector: 'rt-playground-views-datasource',
  imports: [PushPipe, Select2Component, ReactiveFormsModule, DetachedRouterOutlet],
  template: `
    <div layout margin-bottom>
      <rt-select2
        [items]="(dataSourceItems$ | ngrxPush) ?? []"
        [formControl]="dataSourceControl"
        style="width: 230px"
      />
    </div>

    <div class="main">
      <rtc-detached-router-outlet />
    </div>
  `,
  styles: [
    `
      :host {
        width: 100%;
      }

      .main {
        height: calc(100vh - (4.8rem + 3.6rem + 2rem));
      }
    `,
  ],
})
export class DataSourcePlaygroundComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);

  public dataSourceItems$: Observable<Array<SelectOption<string>>> = this.store
    .select(DataSourceListSelectors.data)
    .pipe(
      filterBoolean,
      map(list => list.map(item => ({ label: item.id, value: item.id }))),
      tap(([option]) => {
        if (this.dataSourceControl.value) return;

        const dataSourceId = option.value;
        this.dataSourceControl.setValue(dataSourceId);
      }),
    );
  public dataSourceControl = new FormControl<string>(inject(INITIAL_DS_ID));

  private untilDestroyed = injectUntilDestroyed();
  private dataSourceProvider$ = inject(dataSourceProvider$);
  private dataSourceServiceProvider$ = inject(dataSourceServiceProvider$);
  private dataSourceServiceMap = Object.fromEntries(
    Object.entries(inject(dataSourceServiceMap)).map(([dataSourceId, service]) => [
      dataSourceId,
      inject(service),
    ]),
  );

  public ngOnInit() {
    this.store.dispatch(DataSourceListActions.fetch());

    this.dataSourceControl.valueChanges
      .pipe(filterBoolean, this.untilDestroyed)
      .subscribe(dataSourceId => {
        this.dataSourceServiceProvider$.next(this.dataSourceServiceMap[dataSourceId]);
        this.loadDataSourceData(dataSourceId);
      });

    this.dataSourceControl.valueChanges
      .pipe(
        startWith(this.dataSourceControl.value),
        filterBoolean,
        switchMap(dataSourceId => this.store.select(DataSourceSelectors.byId(dataSourceId))),
        filterBoolean,
        this.untilDestroyed,
      )
      .subscribe(dataSource => {
        this.dataSourceProvider$.next(dataSource);
      });
  }

  public ngOnDestroy() {
    this.dataSourceProvider$.complete();
  }

  public loadDataSourceData(dataSourceId: string) {
    this.store.dispatch(DataSourceActions.fetchOne({ payload: { id: dataSourceId } }));
    this.store.dispatch(ViewListActions.fetchCollection({ payload: { dataSourceId } }));
  }
}
